import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_URL || 'https://masterfileai-api.azurewebsites.net';
const NAVY = '#0a2540';
const ORANGE = '#F37428';

export default function Unsubscribe() {
  const [params] = useSearchParams();
  // loading | confirm | already | confirming | success | error
  const [status, setStatus] = useState('loading');
  const [name, setName] = useState('');

  const uid = params.get('uid');

  useEffect(() => {
    if (!uid) { setStatus('error'); return; }

    fetch(`${API_BASE}/api/console/b2b/unsubscribe?uid=${encodeURIComponent(uid)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok) { setStatus('error'); return; }
        setName(d.name || '');
        // Set tracking cookie so we can identify them if they visit the marketing site
        if (d.ux_token) {
          const exp = new Date(Date.now() + 365 * 864e5).toUTCString();
          document.cookie = `mf_ux=${encodeURIComponent(d.ux_token)};expires=${exp};path=/;domain=.masterfileai.com;SameSite=Lax`;
        }
        setStatus(d.already ? 'already' : 'confirm');
      })
      .catch(() => setStatus('error'));
  }, []);

  function confirm() {
    setStatus('confirming');
    fetch(`${API_BASE}/api/console/b2b/unsubscribe`, {
      method:  'POST',
      headers: { 'Content-Type': 'text/plain' },
      body:    JSON.stringify({ uid }),
    })
      .then(r => r.json())
      .then(d => setStatus(d.ok ? 'success' : 'error'))
      .catch(() => setStatus('error'));
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.bar} />
        <div style={s.body}>
          <div style={s.logo}>MasterFileAI</div>

          {(status === 'loading' || status === 'confirming') && (
            <p style={s.msg}>Processing your request…</p>
          )}

          {status === 'confirm' && (
            <>
              <div style={{ ...s.icon, background: 'rgba(243,116,40,0.1)', color: ORANGE }}>✉</div>
              <h1 style={s.heading}>Unsubscribe?</h1>
              <p style={s.msg}>
                {name ? `Hi ${name} — are` : 'Are'} you sure you want to stop receiving marketing emails from MasterFileAI?
              </p>
              <button onClick={confirm} style={s.btn}>
                Yes, unsubscribe me
              </button>
              <p style={s.sub}>This action cannot be undone from this page.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={s.icon}>✓</div>
              <h1 style={s.heading}>You've been unsubscribed</h1>
              <p style={s.msg}>
                {name ? `${name}, you` : 'You'} won't receive any more marketing emails from MasterFileAI.
              </p>
              <p style={s.sub}>
                Changed your mind? Reply to any previous email and we'll re-add you.
              </p>
            </>
          )}

          {status === 'already' && (
            <>
              <div style={s.icon}>✓</div>
              <h1 style={s.heading}>Already unsubscribed</h1>
              <p style={s.msg}>
                {name ? `${name}, you're` : "You're"} already unsubscribed from MasterFileAI marketing emails.
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{ ...s.icon, background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>✕</div>
              <h1 style={s.heading}>Invalid link</h1>
              <p style={s.msg}>
                This unsubscribe link is not valid. Please use the link from your email.
              </p>
              <p style={s.sub}>
                If you're still receiving unwanted emails, reply directly and we'll remove you manually.
              </p>
            </>
          )}

          <a href="https://www.masterfileai.com" style={s.link}>← Back to MasterFileAI.com</a>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#f0f2f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Arial, Helvetica, sans-serif',
    padding: '40px 16px',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 480,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  bar: { height: 6, background: ORANGE },
  body: { padding: '48px 40px', textAlign: 'center' },
  logo: { color: NAVY, fontWeight: 800, fontSize: 18, letterSpacing: '-0.5px', marginBottom: 32 },
  icon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'rgba(5,150,105,0.1)',
    color: '#059669',
    fontSize: 24,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  heading: { color: NAVY, fontSize: 22, fontWeight: 700, margin: '0 0 12px' },
  msg: { color: '#4b5563', fontSize: 16, lineHeight: 1.6, margin: '0 0 16px' },
  sub: { color: '#9ca3af', fontSize: 13, lineHeight: 1.6, margin: '16px 0 32px' },
  link: { color: ORANGE, fontSize: 13, fontWeight: 600, textDecoration: 'none' },
  btn: {
    background: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 28px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'block',
    margin: '0 auto',
  },
};
